import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import PageLoader from './PageLoader';

const RentalSO = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchRentalOrders();
  }, []);

  const fetchRentalOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/rental/pending`);

      if (response.data.success) {
        setOrders(response.data.orders);
      } else {
        setError('Failed to fetch rental orders');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching rental orders:', err);
      setError('เกิดข้อผิดพลาดในการดึงข้อมูลเอกสารเครื่องเช่า');
      setLoading(false);
    }
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      (order.venio_so_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.customerName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.customerCode || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Calculate completion percentage
  const getCompletionPercentage = (order) => {
    let completed = 0;
    let total = 4;

    if (order.contact_name) completed++;
    if (order.contract_name) completed++;
    if (order.location_province) completed++;
    if (order.contract_start_date) completed++;

    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return <PageLoader message="กำลังโหลดข้อมูลเอกสารเครื่องเช่า..." />;
  }

  return (
    <div className="rental-so-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">เอกสารเครื่องเช่า</h1>
          <p className="page-subtitle">จัดการและติดตามเอกสารเครื่องเช่าที่รอดำเนินการ</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/rental-assets" className="btn-secondary">
            <i className="bi bi-table"></i>
            รายการเครื่องเช่า (Serial)
          </Link>
          <button onClick={fetchRentalOrders} className="btn-secondary">
            <i className="bi bi-arrow-clockwise"></i>
            รีเฟรช
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <div className="search-input-wrapper">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="search-input"
                placeholder="ค้นหา SO Number, ชื่อลูกค้า, รหัสลูกค้า..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="filter-group">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">ทุกสถานะ</option>
              <option value="pending">รอดำเนินการ</option>
              <option value="completed">เสร็จสิ้น</option>
            </select>
          </div>

          <div className="results-count">
            {filteredOrders.length} รายการ
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert-error">
          <i className="bi bi-exclamation-triangle-fill"></i>
          {error}
        </div>
      )}

      {/* Table */}
      {!error && filteredOrders.length === 0 ? (
        <div className="empty-state">
          <i className="bi bi-inbox"></i>
          <h3>ไม่พบเอกสารเครื่องเช่า</h3>
          <p>ยังไม่มีคำสั่งซื้อเครื่องเช่าที่รอดำเนินการในขณะนี้</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>SO Number</th>
                  <th>ลูกค้า</th>
                  <th>รหัสลูกค้า</th>
                  <th>วันที่</th>
                  <th className="text-right">ยอดรวม</th>
                  <th className="text-center">ความคืบหน้า</th>
                  <th className="text-center">สถานะ</th>
                  <th className="text-center">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((order) => {
                  const completion = getCompletionPercentage(order);
                  return (
                    <tr key={order.id}>
                      <td>
                        <div className="cell-with-icon">
                          <i className="bi bi-file-earmark-text"></i>
                          <span className="font-medium">{order.venio_so_number || '-'}</span>
                        </div>
                      </td>
                      <td>{order.customerName || '-'}</td>
                      <td>
                        <span className="text-muted">{order.customerCode || '-'}</span>
                      </td>
                      <td>
                        {order.orderDate
                          ? new Date(order.orderDate).toLocaleDateString('th-TH', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                          : new Date(order.created_at).toLocaleDateString('th-TH', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                      </td>
                      <td className="text-right">
                        <span className="amount">
                          ฿{parseFloat(order.grandTotal || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td>
                        <div className="progress-cell">
                          <div className="progress-bar-wrapper">
                            <div className="progress-bar" style={{ width: `${completion}%` }}></div>
                          </div>
                          <span className="progress-text">{completion}%</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge badge-warning">
                          รอดำเนินการ
                        </span>
                      </td>
                      <td className="text-center">
                        <a href={`/rental-so/${order.id}`} className="btn-action">
                          <i className="bi bi-pencil-square"></i>
                          กรอกข้อมูล
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
                ก่อนหน้า
              </button>

              <div className="pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        className={`pagination-page ${page === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="pagination-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ถัดไป
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RentalSO;
